import type { MovingRect } from './canvasAlignment'
import type { ShoveAxis } from './canvasShove'
import { SPAWN_GAP, viewOf } from './canvasSpawn'
import type { SwapEntry } from './canvasSwap'

/**
 * Die **Leerplätze** am **Tauschpunkt** (CONTEXT.md): die freien Felder
 * neben einem Zettel, in denen der Getragene liegen kann, **ohne dass jemand
 * rückt**. Das dritte Ziel neben Kern (tauschen) und Rand (einreihen) — und
 * das Werkzeug, mit dem man ein Raster überhaupt erst baut.
 *
 * **Acht Felder je Zettel, eines weit**: vier Seiten und vier Ecken. Wo das Feld liegt, sagt das
 * Raster, das schon dasteht: Gibt es auf dieser Seite eine benachbarte Spalte
 * (oder Reihe), liegt das Feld in ihrer Flucht; außerhalb des Rasters im
 * Rhythmus dieser Achse — dem Abstand zur nächsten Spalte —, und ohne jede
 * Spalte im üblichen Takt. **Enger als ein Takt nie**, in keiner der Lagen. Gelesen, nicht gerechnet: In einem von Hand
 * gebauten Raster landet der Zettel so in der Flucht.
 *
 * Kein Feld, wo ein Zettel liegt oder hineinragt, wo der Getragene nicht
 * hineinpasst, und keines auf seinem eigenen Ursprung: Der ist **Daheim**
 * (CONTEXT.md) — ein Ziel für sich, das die Geste selbst anbietet
 * (`useSwapDrag`, `kind: 'home'`), und dort ändert Loslassen nichts. Als Feld
 * wäre er auch aus der Ferne einmal das nächstgelegene (`nearest`), und
 * `getCloseOriginPlan` ließe hinter ihm eine Reihe zugehen, die es nicht gibt.
 * Reines Rechenwerk; getroffen wird in `canvasSwapZones.ts`.
 */

export interface FreeSlot {
  /** Der Zettel, an dessen Seite das Feld hängt (bei mehreren Anbietern der erste). */
  owner: SwapEntry
  /** Das Feld, in Zettelgröße des Getragenen — Canvas-Koordinaten. */
  rect: MovingRect
}

/** Eine **Spalte** oder **Reihe** des Rasters: ein Band aus quer überlappenden Zetteln. */
interface Band {
  start: number
  end: number
}

/**
 * Die Bänder einer Achse: Zettel, die sich auf ihr überlappen, bilden eine
 * Spalte (x) bzw. Reihe (y). Nur ECHTE Überlappung zählt, wie bei der
 * Reihen-Regel: Bündig aneinanderstoßende Zettel sind Nachbarn, keine Spalte.
 */
function bandsOf(entries: readonly SwapEntry[], axis: ShoveAxis): Band[] {
  const spans = entries
    .map(entry => {
      const v = viewOf(entry.rect, axis === 'x')
      return { start: v.lead, end: v.trail }
    })
    .sort((a, b) => a.start - b.start)
  const bands: Band[] = []
  for (const span of spans) {
    const last = bands[bands.length - 1]
    if (last && span.start < last.end) last.end = Math.max(last.end, span.end)
    else bands.push({ ...span })
  }
  return bands
}

const bandIndexOf = (bands: Band[], lead: number) =>
  bands.findIndex(band => lead >= band.start && lead < band.end)

/**
 * Wo auf dieser Achse das Feld neben dem Zettel beginnt (seine führende
 * Kante), für die Seite `after` (rechts/unten) oder `before` (links/oben).
 *
 * ZWEI Lagen, beide gültig:
 * 1. **Flucht** — die Nachbarspalte liegt dicht (Lücke höchstens eine
 *    Feldlänge): Das Feld beginnt an ihrer Kante. So findet ein Loch MITTEN
 *    im Raster seinen Platz.
 * 2. **Takt** — das Feld hängt sich an SEINEN Zettel, im Abstand der Achse
 *    (die Lücke zur Spalte auf der anderen Seite), sonst im üblichen Takt.
 *
 * **Beide, nicht nur die erste.** Ist die Flucht besetzt, gäbe es in dieser
 * Richtung sonst gar kein Feld — der Fall des Nutzers („links, unten und
 * rechts gehen, oben nicht"), weil über dem Raster eine Reihe lag. Wo beide
 * frei sind, stehen beide zur Wahl; unter dem Zeiger gewinnt ohnehin das
 * Feld mit der näheren Mitte.
 *
 * **Mittig in die Lücke gehört es nicht.** Das war einmal so, für Lücken,
 * die mehr als ein Feld fassen — und es kostete den Fall aus dem Bild des
 * Nutzers: Lag über einer Reihe weit oben noch ein Zettel, sprang das Feld
 * auf halbe Höhe dazwischen, dorthin, wo niemand es vermutet. Wer über einer
 * Reihe ablegt, meint den Platz DIREKT über ihr.
 */
function leadsOf(
  me: { lead: number; trail: number },
  bands: Band[],
  mine: number,
  side: 'before' | 'after',
  length: number,
): number[] {
  const next = side === 'after' ? bands[mine + 1] : bands[mine - 1]
  const previous = side === 'after' ? bands[mine - 1] : bands[mine + 1]
  const own = bands[mine]!
  // Die Lücke zwischen der eigenen Spalte und einer anderen — auf welcher Seite sie auch liegt.
  const gapTo = (band: Band) => (band.start >= own.end ? band.start - own.end : own.start - band.end)

  /*
    **Enger als ein Takt legt der Canvas nie an.** Der Rhythmus wird gelesen,
    nicht gerechnet — er kommt aus dem Abstand zur Spalte auf der anderen
    Seite, damit ein von Hand gebautes Raster seinen eigenen behält (47px
    bleiben 47). Nach UNTEN gibt es aber einen Boden: Liegen dort zwei Reihen
    dicht beieinander, erbte das Feld diesen Krümel und klebte an seinem
    Zettel. Gemeldet 2026-09-04 mit Bild — der Zettel landete bündig auf der
    roten Karte, „die üblichen 24px fehlen".
  */
  const gelesen = previous && gapTo(previous) <= length ? gapTo(previous) : SPAWN_GAP
  const rhythm = Math.max(gelesen, SPAWN_GAP)
  const takt = side === 'after' ? me.trail + rhythm : me.lead - rhythm - length

  const leads: number[] = []
  if (next && gapTo(next) <= length) {
    const lead = side === 'after' ? next.start : next.end - length
    /*
      **Näher als der Rhythmus rückt kein Feld.** Die Flucht richtet sich nach
      der Nachbarspalte — steht die aber dicht am eigenen Zettel, klebte das
      Feld an ihm, ganz ohne **Takt**. Gemeldet 2026-09-04: Ein Zettel über
      der Kopfzeile (in einer NACHBARSPALTE, dicht darüber) ließ das Feld über
      der roten Karte bündig an ihr aufsetzen. Steht die Nachbarspalte dicht,
      gilt darum der Takt-Vorschlag allein — er trägt denselben Rhythmus wie
      der Rest der Achse.
    */
    const gap = side === 'after' ? lead - me.trail : me.lead - (lead + length)
    if (gap >= rhythm) leads.push(lead)
  }
  if (!leads.some(lead => Math.abs(lead - takt) < 0.5)) leads.push(takt)
  return leads
}

const overlaps = (a: MovingRect, b: MovingRect) =>
  a.x < b.x + b.width && a.x + a.width > b.x && a.y < b.y + b.height && a.y + a.height > b.y

const samePlace = (a: MovingRect, b: MovingRect) => Math.abs(a.x - b.x) < 0.5 && Math.abs(a.y - b.y) < 0.5

/**
 * Alle Leerplätze für den Getragenen — je anderer Zettel ACHT: seine vier
 * Seiten und seine vier Ecken. Verworfen, wenn dort etwas liegt, hineinragt
 * oder der Ursprung ist; gleiche Felder mehrerer Anbieter nur einmal.
 *
 * **Warum die Ecken dazugehören** (Zielbild des Nutzers, 2026-09-04): Ohne
 * sie hat der Ring um eine Gruppe vier Löcher — die Eckfelder hat kein
 * Zettel als orthogonalen Nachbarn. Und ein zweites Paar Löcher entsteht
 * neben dem Platz, den der Getragene gerade verlässt: Dort wäre er selbst
 * der Anbieter gewesen, und er ist aus der Liste genommen. Beides schließt
 * die Diagonale, ohne eine neue Rechnung: Ein Eckfeld ist die Paarung der
 * beiden Achsen-Lagen, die es ohnehin gibt.
 */
export function freeSlotsFor(entries: readonly SwapEntry[], dragged: string): FreeSlot[] {
  const moved = entries.find(entry => entry.id === dragged)
  if (!moved) return []
  const others = entries.filter(entry => entry.id !== dragged)
  const size = { width: moved.rect.width, height: moved.rect.height }
  /*
    **Das Raster OHNE den Getragenen.** Er ist im Weggehen — seine eigene
    Spalte und Zeile dürfen nicht bestimmen, wo er landen darf. Sonst gäbe der
    Abstand, den er zufällig gerade zu einer Reihe hat, den Rhythmus vor: Über
    einer Dreierreihe entstünde ein einziger Platz in seiner alten Zeile,
    statt über jedem der drei einer im **Takt** der Reihe.
  */
  const columns = bandsOf(others, 'x')
  const rows = bandsOf(others, 'y')

  /*
    **Der verlassene Platz bietet weiter an.** Der Getragene zählt für die
    Spalten und Zeilen nicht mit (siehe oben) — als ANKER aber schon: Sonst
    fehlte der Ecke des Rings ausgerechnet das Feld schräg neben dem Loch,
    das er gerade hinterlässt. Nur wenn sein Platz überhaupt in einer Spalte
    UND einer Zeile der übrigen liegt: Ein Zettel, der ganz für sich steht,
    gäbe sonst Maße vor, die es im Raster nicht gibt.
  */
  const imRaster = bandIndexOf(columns, moved.rect.x) >= 0 && bandIndexOf(rows, moved.rect.y) >= 0
  const anker = imRaster ? [...others, moved] : others

  const slots: FreeSlot[] = []
  for (const owner of anker) {
    const column = bandIndexOf(columns, owner.rect.x)
    const row = bandIndexOf(rows, owner.rect.y)
    // Die Lage je Achse und Seite — daraus die vier Seiten und die vier Ecken.
    const xs = (['before', 'after'] as const).flatMap(side =>
      leadsOf(viewOf(owner.rect, true), columns, column, side, size.width))
    const ys = (['before', 'after'] as const).flatMap(side =>
      leadsOf(viewOf(owner.rect, false), rows, row, side, size.height))
    const candidates: MovingRect[] = [
      ...xs.map(x => ({ x, y: owner.rect.y, ...size })),
      ...ys.map(y => ({ x: owner.rect.x, y, ...size })),
      ...xs.flatMap(x => ys.map(y => ({ x, y, ...size }))),
    ]
    for (const rect of candidates) {
      // Nur SEIN Platz ist verboten, nicht jede Überlappung mit ihm: Er zieht
      // ja weg, und ein Feld, das seinen alten Platz streift, ist trotzdem ein
      // anderer Ort.
      if (samePlace(rect, moved.rect)) continue
      if (others.some(other => overlaps(rect, other.rect))) continue
      if (slots.some(slot => samePlace(slot.rect, rect))) continue
      slots.push({ owner, rect })
    }
  }
  return slots
}
